import React from "react";
import {
  Heart,
  Utensils,
  Droplets,
  Activity,
  AlertTriangle,
  Crosshair,
  ShoppingCart,
} from "lucide-react";
import { callBackend } from "../utils/api";

export default function SurvivalManagement({ stats, fetchStats }) {
  const getStatTheme = (val) => {
    if (val > 70)
      return {
        text: "text-green-500",
        bar: "bg-green-500",
        glow: "shadow-[0_0_10px_#22c55e]",
      };
    if (val > 30)
      return {
        text: "text-yellow-500",
        bar: "bg-yellow-500",
        glow: "shadow-[0_0_10px_#eab308]",
      };
    return {
      text: "text-red-500 animate-pulse",
      bar: "bg-red-500",
      glow: "shadow-[0_0_10px_#ef4444]",
    };
  };

  const handleConsume = async (invId) => {
    try {
      await callBackend("/items/consume", {
        method: "POST",
        body: { inventoryId: invId },
      });
      fetchStats();
    } catch (err) {
      console.error(err);
    }
  };

  const consumables = (stats.inventory || []).filter(
    (i) => i.type === "consumable" && i.quantity > 0,
  );

  const StatScanner = ({ label, value, icon: Icon }) => {
    const theme = getStatTheme(value);
    return (
      <div className="relative bg-zinc-950 border border-zinc-900 p-4">
        <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-zinc-700"></div>
        <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-zinc-700"></div>
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 bg-zinc-900 border border-zinc-800 ${theme.text}`}
            >
              <Icon size={18} />
            </div>
            <div>
              <div className="text-[10px] font-black text-zinc-500 uppercase tracking-tighter">
                {label}
              </div>
              <div
                className={`text-2xl font-black italic leading-none ${theme.text}`}
              >
                {value}%
              </div>
            </div>
          </div>
        </div>
        <div className="relative h-1.5 w-full bg-zinc-900 overflow-hidden">
          <div
            className={`h-full transition-all duration-1000 ease-out ${theme.bar} ${theme.glow}`}
            style={{ width: `${value}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 font-mono select-none animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 border-b-2 border-zinc-900 pb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Activity className="text-green-500 animate-pulse" size={24} />
            <h2 className="text-4xl font-black italic uppercase text-white tracking-tighter">
              Biometrics
            </h2>
          </div>
          <p className="text-zinc-600 text-[9px] font-black uppercase tracking-[0.3em] flex items-center gap-2">
            <Crosshair size={10} /> Neural Link: {stats.username}
            {" // "}ID_
            {stats.id.toString().padStart(4, "0")}
          </p>
        </div>
        <div
          className={`border-2 px-4 py-2 font-black italic tracking-tighter text-sm uppercase skew-x-[-12deg] 
          ${stats.state === "alive" ? "border-green-900 bg-green-900/10 text-green-500" : "border-red-900 bg-red-900/10 text-red-500 animate-pulse"}`}
        >
          Status: {stats.state}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatScanner label="Vitals" value={stats.health} icon={Heart} />
        <StatScanner label="Nutrition" value={stats.hunger} icon={Utensils} />
        <StatScanner label="Hydration" value={stats.thirst} icon={Droplets} />
      </div>

      <div className="bg-zinc-950 border border-zinc-900 p-4">
        <div className="text-[10px] font-black text-zinc-500 uppercase mb-4 flex items-center gap-2">
          <ShoppingCart size={12} /> Personal Supply / Consumables
        </div>
        {consumables.length === 0 ? (
          <p className="text-[9px] text-zinc-700 italic">
            No supplies in stash.
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {consumables.map((item) => (
              <button
                key={item.id}
                onClick={() => handleConsume(item.id)}
                className="bg-zinc-900 border border-zinc-800 p-2 text-left hover:border-green-600 transition-all group"
              >
                <div className="text-[10px] text-white font-black truncate">
                  {item.name}
                </div>
                <div className="text-[8px] text-zinc-500 uppercase">
                  Qty: {item.quantity}
                  {" // "}
                  <span className="text-green-500 group-hover:animate-pulse">
                    USE
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-zinc-950 border-2 border-red-900/30 p-4 flex items-start gap-4">
        <div className="p-2 bg-red-900/20 border border-red-900/50 text-red-500">
          <AlertTriangle size={20} />
          <p className="text-[10px] text-zinc-500 font-bold uppercase leading-tight">
            Warning: Internal system failure is imminent if nutrition or
            hydration reach 0%. Flatlining leads to immediate street asset
            forfeiture.
          </p>
        </div>
      </div>
    </div>
  );
}
